import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Popconfirm } from 'antd';
import { debounce } from 'debounce';

import { CustomModal as Modal } from './CopyDataFromAnotherCardModal.style';
import translationNewLineSeparator from '../../../helpers/translationNewLineSeparator';

const { Option } = Select;

export interface Props {
  /** lista kart przedmiotów */
  cardList: any[];
  /** stan otwarcia modala */
  opened: boolean;
  /** stan ładowania modala */
  loading: boolean;
  /** wybrany element */
  selected: number | null;
  /** callback wykonywany przy wciśnięciu przycisku potwierdzającego */
  onOk: () => void;
  /** callback wykonywany przy wciśnięciu przycisku anulowania / zamknięcia modala */
  close: () => void;
  /** callback do zarządzania wyborem opcji na komponencie Select */
  select: (id: SubjectCardData['przedmiotId']) => void;
  /** callback służący do przekazania szukanej frazy */
  setSearchCopyCardsData: (search: string) => void;
}

/**
 * Komponent modala do kopiowania karty przedmiotu z innej karty
 *
 */
export function CopyDataFromAnotherCardModal(props: Props): React.ReactElement {
  const { t } = useTranslation();
  const { cardList, opened, loading, selected, close, onOk, select, setSearchCopyCardsData } = props;
  const codeInput = useRef<any>();
  useEffect(() => {
    setTimeout(() => {
      if (opened) {
        codeInput.current.focus();
      }
    }, 200);
  }, [opened]);
  const [selectedData, setSelectedData] = useState<any>(null);
  const onInputFocus = (input: any): void => {
    codeInput.current = input;
  };
  const onChange = (value: number): void => {
    select(value);
    const selectedData = cardList.find(item => item.przedmiotId === value);
    setSelectedData(selectedData);
  };
  const onSearch = (phrase: string): void => {
    const trimmed = phrase.trim();
    if (trimmed.length > 2) {
      setSearchCopyCardsData(trimmed);
    }
  };

  return (
    <Modal
      visible={opened}
      okText={
        <Popconfirm
          id='copyDataFromAnotherCardModalPopconfirm'
          title={translationNewLineSeparator(t('COPY_CARD_POPCONFIRM_TEXT'))}
          okText={t('YES')}
          cancelText={t('NO')}
          onConfirm={onOk}
          overlayClassName='custom_popconfirm'
        >
          {t('CONFIRM')}
        </Popconfirm>
      }
      cancelText={t('CANCEL')}
      okButtonProps={{ disabled: !selected }}
      onCancel={close}
      confirmLoading={loading}
      title={t('COPY_SUBJECT_CARD_MODAL_TITLE')}
      destroyOnClose
      className='cyCopyDataFromAnotherCardModal'
    >
      <h3>{t('COPY_SUBJECT_CARD_MODAL_SELECT')}</h3>
      <Select
        filterOption={() => true}
        loading={loading}
        showSearch
        placeholder={t('START_TYPING')}
        onChange={onChange}
        notFoundContent={t('NO_DATA')}
        onSearch={debounce(onSearch, 200)}
        dropdownClassName='custom_dropdown'
        className='custom_select'
        style={{ width: '100%' }}
        value={selected ? selected : undefined}
        ref={onInputFocus}
        data-cy='selectCardToCopy'
      >
        {cardList.map((card: any, idx) => (
          <Option
            key={card.przedmiotId}
            value={card.przedmiotId}
            className='custom_option'
            title={`${card.przedmiotNazwa}(${card.przedmiotKod})`}
            data-cy={`optionCardToCopy_${idx}`}
          >
            {`${card.przedmiotNazwa} (${card.przedmiotKod})`}
          </Option>
        ))}
      </Select>
      <article>
        {selectedData ? (
          <span>
            <strong>{t('COPY_SUBJECT_CARD_MODAL_SELECTED_CARD')}</strong>
            :&nbsp;
            {selectedData.przedmiotNazwa} ({selectedData.przedmiotKod})
          </span>
        ) : null}
      </article>
    </Modal>
  );
}

export default CopyDataFromAnotherCardModal;
